import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash, Plus } from "lucide-react";
import MainLayout from "@/components/layouts";
import { useTaskWorkflow } from "./task-workflow.hooks";
import { useParams } from "react-router";
import { uuid } from "@/interfaces";
import AddTaskWorkflowModal from "./task-workflow.components/add-category";
import { DeletePopover } from "@/components/shared/delete-popover";

export default function TaskWorkflow() {
  const { id } = useParams();
  const {
    list,
    loading,
    newTask,
    setNewTask,
    handleAddTask,
    addDialog,
    setAddDialog,
    handleSubmit,
    handleDeleteTask,
    handleDeleteCategory,
  } = useTaskWorkflow(id as uuid);

  const layoutProps = {
    loading,
    pageHeader: {
      title: "Task Workflow",
      subtitle: "Manage your services task workflow",
      buttonText: "Add Category",
      onButtonClick() {
        setAddDialog(true);
      },
    },
    breadcrumb: [
      {
        name: "Services",
        href: "/services",
      },
      {
        name: "Task Workflow",
        href: "#",
      },
    ],
  };

  return (
    <MainLayout {...layoutProps}>
      <div className="container mx-auto">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 ">
          {list.map(({ id, title, tasks }) => {
            return (
              <Card key={id} className="flex flex-col h-full">
                <CardHeader>
                  <CardTitle className="text-xl">{title}</CardTitle>
                  <CardAction>
                    <DeletePopover
                      title="Delete Task Category."
                      subtitle="Are you sure you want to delete task workflow category and its related tasks?"
                      onDelete={() => handleDeleteCategory(id)}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="cursor-pointer text-red-500 hover:text-red-700 hover:bg-red-100 transition"
                      >
                        <Trash className="h-4 w-4 " />
                      </Button>
                    </DeletePopover>
                  </CardAction>
                </CardHeader>

                <CardContent className="flex-grow flex flex-col justify-between">
                  <ul className="space-y-3 mb-4 flex-grow">
                    {tasks.length === 0 ? (
                      <li className="text-muted-foreground italic">No tasks yet.</li>
                    ) : (
                      tasks.map((task) => (
                        <li
                          key={task.id}
                          className="flex items-center justify-between bg-accent p-3 rounded-md shadow-sm"
                        >
                          <span className="text-sm flex-grow pr-2">{task.title}</span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteTask(task.id)}
                              aria-label={`Delete task: ${task.title}`}
                              className="cursor-pointer text-red-500 hover:text-red-700 hover:bg-red-100 transition"
                            >
                              <Trash className="h-4 w-4 " />
                            </Button>
                          </div>
                        </li>
                      ))
                    )}
                  </ul>
                  <div className="flex gap-2 mt-auto pt-4 border-t border-border">
                    <Input
                      type="text"
                      placeholder="Add new task..."
                      value={newTask[id] || ""}
                      onChange={(e) => setNewTask((prev) => ({ ...prev, [id]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleAddTask(id);
                        }
                      }}
                      aria-label={`New task for ${title}`}
                    />
                    <Button onClick={() => handleAddTask(id)} aria-label={`Add task to ${title}`}>
                      <Plus className="h-4 w-4" />
                      <span className="sr-only">Add Task</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
      <AddTaskWorkflowModal open={addDialog} setOpen={setAddDialog} onSubmit={handleSubmit} />
    </MainLayout>
  );
}
